import { z } from "zod";

const idSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const versionNoSchema = z.number().int().min(1).max(2_147_483_647);
const isoDateTimeSchema = z.string().datetime({ offset: true });
const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Enter a calendar date");
const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u, "Enter a time in 24-hour format");

export const linenServicePlanIdSchema = idSchema;
export const linenCycleIdSchema = idSchema;

export const createLinenServicePlanInputSchema = z.strictObject({
  subscriptionId: idSchema,
  configurationId: idSchema,
  expectedSubscriptionVersionNo: versionNoSchema,
});

const linenPackageReferenceSchema = z.strictObject({
  id: idSchema,
  code: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
});

export const linenServicePlanSchema = z.strictObject({
  id: linenServicePlanIdSchema,
  status: z.enum(["ACTIVE", "CLOSED"]),
  versionNo: versionNoSchema,
  subscriptionId: idSchema,
  subscriptionStatus: z.string().min(1).max(100).nullable(),
  configurationId: idSchema,
  configurationVersionNo: versionNoSchema.nullable(),
  propertyId: idSchema,
  firstDeliveryDate: calendarDateSchema.nullable(),
  deliveryWindowStart: timeSchema.nullable(),
  deliveryWindowEnd: timeSchema.nullable(),
  deliveryTimezone: z.string().min(1).max(64).nullable(),
  package: linenPackageReferenceSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

const linenCycleVisitSchema = z.strictObject({
  id: idSchema,
  status: z.string().min(1).max(100),
  versionNo: versionNoSchema,
});

export const linenCycleSchema = z.strictObject({
  id: linenCycleIdSchema,
  servicePlanId: linenServicePlanIdSchema,
  cycleNo: z.number().int().min(1).max(2_147_483_647),
  kind: z.enum(["INITIAL_DELIVERY", "RECURRING_EXCHANGE"]),
  status: z.string().min(1).max(100),
  scheduledAt: isoDateTimeSchema,
  cleanServiceSetId: idSchema,
  collectionBagId: idSchema.nullable(),
  facilityId: idSchema.nullable(),
  assignedTo: z.string().min(1).max(160).nullable(),
  collectedAt: isoDateTimeSchema.nullable(),
  depositedAt: isoDateTimeSchema.nullable(),
  facilityReceivedAt: isoDateTimeSchema.nullable(),
  qcCompletedAt: isoDateTimeSchema.nullable(),
  cleanReadyAt: isoDateTimeSchema.nullable(),
  versionNo: versionNoSchema,
  visit: linenCycleVisitSchema,
});

export const linenServicePlanListResponseSchema = z.strictObject({
  data: z.array(linenServicePlanSchema),
});
export const linenServicePlanResponseSchema = z.strictObject({
  status: z.literal("ok"),
  servicePlan: linenServicePlanSchema,
});
export const createLinenServicePlanResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  servicePlan: linenServicePlanSchema,
});
export const linenCycleListResponseSchema = z.strictObject({ data: z.array(linenCycleSchema) });
export const linenCycleResponseSchema = z.strictObject({
  status: z.literal("ok"),
  cycle: linenCycleSchema,
});

export type CreateLinenServicePlanInput = z.infer<typeof createLinenServicePlanInputSchema>;
export type LinenServicePlan = z.infer<typeof linenServicePlanSchema>;
export type LinenCycle = z.infer<typeof linenCycleSchema>;
